import { getCachedSession, getJwtToken } from "../lib/auth-client";
import { startSpan, traceparent } from "../lib/trace";
import { SpanKind, SpanStatusCode } from "@opentelemetry/api";

const isDev = import.meta.env.DEV;

/**
 * Error thrown by `customFetch` for non-2xx HTTP responses. Carries the HTTP
 * status so callers can distinguish error classes (e.g. treat 404 as "no
 * resource yet" while surfacing other errors).
 */
export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

function extractMessage(body: unknown): string {
  if (body && typeof body === "object") {
    const obj = body as Record<string, unknown>;
    if (typeof obj.message === "string") return obj.message;
    if (typeof obj.error === "string") return obj.error;
  }
  return "Request failed";
}

/**
 * What to tell a person when the response body is not one of ours.
 *
 * A gateway that cannot reach the service answers with its own HTML page, and
 * that page used to become the error message: an upload dialog rendered a full
 * Cloudflare 502 document where one sentence belonged. Nothing in a body we did
 * not write is safe to show, so the status is all this reads.
 */
function statusMessage(status: number): string {
  if (status === 504 || status === 408) {
    return "The request took too long. Try again.";
  }
  if (status === 502 || status === 503) {
    return "The service is unavailable at the moment. Try again shortly.";
  }
  if (status >= 500) {
    return `Something went wrong on our side (${status}).`;
  }
  return `Request failed (${status}).`;
}

const logRequest = (method: string, url: string) => {
  if (isDev) {
    console.log(`🌐 [API] ${method} ${url}`);
  }
};

const logResponse = (
  method: string,
  url: string,
  status: number,
  durationMs: number,
) => {
  if (isDev) {
    const statusEmoji = status >= 200 && status < 300 ? "✅" : "⚠️";
    console.log(
      `${statusEmoji} [API] ${method} ${url} → ${status} (${durationMs}ms)`,
    );
  }
};

// Statuses that are expected client-side outcomes rather than bugs:
//   401 — session expired / not signed in (handled by auth flow)
//   403 — caller lacks role / site claim (UI should already have hidden the
//         affected action; tolerate the race where it didn't)
//   404 — resource not found (often used as "no value yet" semantics)
// Logging these at error level fills the dev console with red text that
// looks like real failures. Keep them visible at debug level so they're
// still inspectable when needed.
const QUIET_STATUSES = new Set([401, 403, 404]);

const logError = (
  method: string,
  url: string,
  status: number,
  error: unknown,
  durationMs: number,
) => {
  const payload = { status, url, method, error };
  if (QUIET_STATUSES.has(status)) {
    if (isDev) {
      console.debug(
        `ℹ️ [API] ${method} ${url} → ${status} (${durationMs}ms)`,
        payload,
      );
    }
    return;
  }
  console.error(`❌ [API] ${method} ${url} → ${status} (${durationMs}ms)`, payload);
};

export const customFetch = async <T = unknown>(
  url: string,
  options?: RequestInit,
): Promise<T> => {
  const method = options?.method?.toUpperCase() ?? "GET";
  const startTime = performance.now();

  // Ensure we have the latest session and JWT token
  await getCachedSession();

  // Get the JWT token that was extracted from the session response headers
  const token = getJwtToken();

  const headers = new Headers(options?.headers);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  // Inject W3C traceparent so the backend can stitch this request into a
  // trace that spans multiple service hops. When the caller passes a parent
  // span (e.g. an upload flow that issues several requests), we emit a child
  // of it so they share a trace_id; otherwise we mint a fresh one.
  // The outgoing header belongs to the actual exported client span. Starting
  // from an explicit parent keeps multi-request workflows on one trace.
  const { span, context: spanCtx } = startSpan(
    `${method} ${url}`,
    {
    kind: SpanKind.CLIENT,
    attributes: {
      "http.request.method": method,
      "url.path": url,
    },
    },
    options?.parentSpan,
  );
  headers.set("traceparent", traceparent(spanCtx));

  logRequest(method, url);

  // If the signal is already aborted (e.g. React StrictMode unmount), bail out
  // immediately without making the request or logging an error.
  if (options?.signal?.aborted) {
    throw new DOMException("signal is aborted without reason", "AbortError");
  }

  let res: Response;
  try {
    res = await fetch(url, {
      ...options,
      headers,
    });
  } catch (networkError) {
    // AbortError is expected when React unmounts a component mid-request
    // (e.g. StrictMode double-mount, navigation). Don't log it as an error.
    if (
      networkError instanceof DOMException &&
      networkError.name === "AbortError"
    ) {
      span.end();
      throw networkError;
    }
    span.recordException(networkError as Error);
    span.setStatus({ code: SpanStatusCode.ERROR });
    span.end();
    const durationMs = Math.round(performance.now() - startTime);
    console.error(
      `❌ [API] ${method} ${url} → Network Error (${durationMs}ms)`,
      {
        url,
        method,
        error: networkError,
      },
    );
    throw networkError;
  }

  const durationMs = Math.round(performance.now() - startTime);

  // Ended here rather than after body parsing: this span is about the request,
  // and everything below is client-side deserialisation. Ending on the many
  // content-type return paths would be easy to get wrong and leak spans.
  span.setAttribute("http.response.status_code", res.status);
  if (!res.ok) {
    span.setStatus({ code: SpanStatusCode.ERROR });
  }
  span.end();

  if (!res.ok) {
    const errorBody = await res.text();
    let errorData: unknown;
    let message: string;
    try {
      errorData = JSON.parse(errorBody);
      message = extractMessage(errorData);
    } catch {
      // Not our error shape — a proxy, a load balancer, or an empty body. The
      // raw text still rides along on `body` for the console and for anyone
      // debugging; only the message a person reads is replaced.
      errorData = { body: errorBody };
      message = statusMessage(res.status);
    }
    logError(method, url, res.status, errorData, durationMs);
    throw new ApiError(res.status, errorData, message);
  }

  logResponse(method, url, res.status, durationMs);

  // Handle empty responses (e.g., 204 No Content)
  if (res.status === 204 || res.status === 205 || res.status === 304) {
    return {} as T;
  }

  // Check for empty body to avoid JSON parse errors
  const contentLength = res.headers.get("Content-Length");
  if (contentLength === "0") {
    return {} as T;
  }

  // Route by content-type so Orval's typed return values actually hold:
  //   application/json (or unset) → JSON parse (default below)
  //   text/* (e.g. text/event-stream) → raw string
  //   anything else (xlsx, pdf, octet-stream, …) → Blob
  const contentType = res.headers.get("Content-Type") ?? "";
  if (contentType && !contentType.includes("application/json")) {
    if (contentType.startsWith("text/")) {
      return (await res.text()) as T;
    }
    return (await res.blob()) as T;
  }

  // Try to parse JSON, handling empty body edge case
  const text = await res.text();
  if (!text || text.trim() === "") {
    if (isDev) {
      console.warn(`⚠️ [API] ${method} ${url} → Empty response body`);
    }
    return {} as T;
  }

  try {
    return JSON.parse(text) as T;
  } catch (parseError) {
    console.error(`❌ [API] ${method} ${url} → JSON Parse Error`, {
      url,
      method,
      responseText: text.slice(0, 500),
      error: parseError,
    });
    throw new Error(`Failed to parse JSON response from ${url}`);
  }
};
